import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertCircle, Landmark, Download } from 'lucide-react';
import { payrollAPI } from '../services/api';
import { usePayrollCountry } from '../hooks/usePayrollCountry';
import { getStatusInfo, formatDateFr } from '../components/payroll/payrollExportsHelpers';
import PayrollExportsPeriodCard from '../components/payroll/PayrollExportsPeriodCard';
import PayrollExportsOptionsCards from '../components/payroll/PayrollExportsOptionsCards';
import PayrollExportsInstructions from '../components/payroll/PayrollExportsInstructions';

const GULF_COUNTRIES = ['QA', 'SA'];

const PayrollExports = () => {
  const { t } = useTranslation();
  const { countryCode } = usePayrollCountry();
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [exportingCSV, setExportingCSV] = useState(false);
  const [exportingAccounting, setExportingAccounting] = useState(false);
  const [exportingWps, setExportingWps] = useState(false);

  const periodsQ = useQuery({
    queryKey: ['payroll-periods', 'export'],
    queryFn: () =>
      payrollAPI.getPayrollPeriods({ page: 1, limit: 20 }).then((data) =>
        (data.items || []).filter((p) => ['submitted', 'approved', 'closed'].includes(String(p.status || '').toLowerCase()))
      ),
  });

  const periods = periodsQ.data ?? [];

  useEffect(() => {
    if (periodsQ.isError) toast.error(t('payrollExports.toasts.loadError'));
  }, [periodsQ.isError]);

  useEffect(() => {
    if (periods.length > 0 && !selectedPeriodId) {
      const mostRecent = [...periods].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
      setSelectedPeriodId(mostRecent.id.toString());
    }
  }, [periods]);

  const handleExportCSV = async () => {
    if (!selectedPeriodId) { toast.error(t('payrollExports.toasts.selectPeriodFirst')); return; }
    try {
      setExportingCSV(true);
      toast.info(t('payrollExports.toasts.generatingCSV'));
      await payrollAPI.exportPayrollCSV(selectedPeriodId, 'csv');
      toast.success(t('payrollExports.toasts.csvSuccess'));
    } catch (error) {
      toast.error(t('payrollExports.toasts.csvError'));
    } finally {
      setExportingCSV(false);
    }
  };

  const handleExportAccounting = async () => {
    if (!selectedPeriodId) { toast.error(t('payrollExports.toasts.selectPeriodFirst')); return; }
    try {
      setExportingAccounting(true);
      toast.info(t('payrollExports.toasts.generatingAccounting'));
      await payrollAPI.exportAccountingCSV(selectedPeriodId);
      toast.success(t('payrollExports.toasts.accountingSuccess'));
    } catch (error) {
      toast.error(t('payrollExports.toasts.accountingError'));
    } finally {
      setExportingAccounting(false);
    }
  };

  const handleExportWps = async () => {
    if (!selectedPeriodId) { toast.error(t('payrollExports.toasts.selectPeriodFirst')); return; }
    try {
      setExportingWps(true);
      toast.info(t('payrollGulf.wpsToastGenerating'));
      await payrollAPI.exportWpsSif(selectedPeriodId);
      toast.success(t('payrollGulf.wpsToastSuccess'));
    } catch (error) {
      toast.error(t('payrollGulf.wpsToastError'));
    } finally {
      setExportingWps(false);
    }
  };

  const selectedPeriod = periods.find((p) => p.id.toString() === selectedPeriodId);
  const isGulf = GULF_COUNTRIES.includes(countryCode);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold">{t('payrollExports.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('payrollExports.subtitle')}</p>
        </div>
        <Select value={selectedPeriodId} onValueChange={setSelectedPeriodId}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder={t('payrollExports.selectPeriod')} />
          </SelectTrigger>
          <SelectContent>
            {periods.map((period) => (
              <SelectItem key={period.id} value={period.id.toString()}>
                <div className="flex items-center gap-2">
                  {period.description || `${t('payrollExports.from')} ${formatDateFr(period.start_date)}`}
                  <Badge variant={getStatusInfo(period.status).variant} className="text-xs">
                    {getStatusInfo(period.status).label}
                  </Badge>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {periods.length === 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {t('payrollExports.noPeriodsTitle')}{' '}
            {t('payrollExports.noPeriodsDesc')}
          </AlertDescription>
        </Alert>
      )}

      {selectedPeriod && (
        <>
          <PayrollExportsPeriodCard selectedPeriod={selectedPeriod} />
          <PayrollExportsOptionsCards
            selectedPeriod={selectedPeriod}
            exportingCSV={exportingCSV}
            handleExportCSV={handleExportCSV}
            exportingAccounting={exportingAccounting}
            handleExportAccounting={handleExportAccounting}
          />
          {isGulf && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Landmark className="h-5 w-5" />
                  {t('payrollGulf.wpsTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {countryCode === 'QA' ? t('payrollGulf.wpsDescQA') : t('payrollGulf.wpsDescSA')}
                </p>
                <Button onClick={handleExportWps} disabled={exportingWps}>
                  <Download className="h-4 w-4 mr-2" />
                  {exportingWps ? t('payrollGulf.wpsGenerating') : t('payrollGulf.wpsDownload')}
                </Button>
              </CardContent>
            </Card>
          )}
          <PayrollExportsInstructions />
        </>
      )}
    </div>
  );
};

export default PayrollExports;
